import type { Bereich, Modul, NavEintrag } from "./modul";
import type { Recht } from "./rechte";

interface BereichDef {
  id: Bereich;
  /** Sichtbarer Titel über den Einträgen, null ohne Titel */
  titel: string | null;
  /** Name für Screenreader (aria-label der Navigation) */
  name: string;
  /** Kopf als Knopf zum Auf- und Zuklappen (BAUPLAN-SEITENLEISTE.md, Abschnitt 5) */
  aufklappbar?: boolean;
  /** Am unteren Ende der Seitenleiste über einer Linie, ohne Titel (Abschnitt 6) */
  fuss?: boolean;
}

/** Bereiche der Seitenleiste (BAUPLAN-PAKETE.md, Abschnitt 5.1, seit 16.10. kompakt nach BAUPLAN-SEITENLEISTE.md): Meine Woche, Sprint,
 *  Aufgaben und Vorhaben ohne Titel, Vorstand und Verein mit leisem Titel, Redaktion zum Aufklappen, Zugänge unten. „unten“ (Mitteilungen)
 *  steht in der Kopfzeile, nicht in der Leiste. */
export const BEREICHE: readonly BereichDef[] = [
  { id: "start", titel: null, name: "Start" },
  { id: "aufgaben", titel: null, name: "Aufgaben" },
  { id: "vorstand", titel: "Vorstand", name: "Vorstand" },
  { id: "verein", titel: "Verein", name: "Verein" },
  { id: "redaktion", titel: "Redaktion", name: "Redaktion", aufklappbar: true },
  { id: "verwaltung", titel: null, name: "Verwaltung", fuss: true },
  { id: "unten", titel: null, name: "Unten" },
];

/** Eintrag der Seitenleiste. Bei einer Gruppe steht ihr Name als `label`, `pfade` sind alle erlaubten Seiten der Gruppe. */
export type NavPunkt = NavEintrag & { pfade: string[] };

/** Reiter einer Gruppe: je erlaubter Seite einer */
export interface GruppenReiter {
  label: string;
  pfad: string;
}

const sortiert = (a: NavEintrag, b: NavEintrag) => (a.reihenfolge ?? 50) - (b.reihenfolge ?? 50) || a.label.localeCompare(b.label, "de");

/** Darf die Person den Eintrag sehen? Mit Recht, und ohne das Recht, das ihn ausblendet. */
function sichtbar(e: NavEintrag, rechte: ReadonlySet<Recht>): boolean {
  return (!e.recht || rechte.has(e.recht)) && (!e.ohneRecht || !rechte.has(e.ohneRecht));
}

/** Navigation je Rolle: nur Einträge, für die die Person das Recht hat. Leere Bereiche entfallen. Einträge einer Gruppe werden zu
 *  einem, der zur ersten erlaubten Seite führt. Aufklappbar ist ein Bereich nur, wenn die Person auch den Bereich „Vorstand“ sieht: Wer
 *  nur die Redaktion hat, sieht sie offen und ohne Pfeil, weil es sonst kaum etwas gibt (BAUPLAN-SEITENLEISTE.md, Abschnitt 5). */
export function navigationFuer(module: readonly Modul[], rechte: ReadonlySet<Recht>) {
  // Erst nach Recht filtern, dann nach Pfad: Zwei Einträge mit demselben Pfad, die sich ausschließen (recht, ohneRecht), stören sich
  // nicht. Gleicher Pfad und beide erlaubt: das spätere Modul gewinnt (wie bei den Routen).
  const nachPfad = new Map<string, NavEintrag>();
  for (const e of module.flatMap((m) => m.navigation)) if (sichtbar(e, rechte)) nachPfad.set(e.pfad, e);
  const erlaubt = [...nachPfad.values()];

  const gruppen = new Map<string, GruppenReiter[]>();
  for (const e of [...erlaubt].sort(sortiert)) {
    if (!e.gruppe) continue;
    gruppen.set(e.gruppe, [...(gruppen.get(e.gruppe) ?? []), { label: e.label, pfad: e.pfad }]);
  }

  // Einträge, die einen Dialog öffnen (Neue Aufgabe), stehen nur in der unteren Leiste. Auf jeder Seite gibt es dafür einen Knopf.
  const belegt = BEREICHE.map((b) => ({ ...b, eintraege: punkteAus(erlaubt.filter((e) => e.bereich === b.id && !e.dialog).sort(sortiert)) })).filter(
    (b) => b.eintraege.length > 0,
  );
  const mitVorstand = belegt.some((b) => b.id === "vorstand");
  const bereiche = belegt.map((b) => ({ ...b, aufklappbar: !!b.aufklappbar && mitVorstand }));
  return { bereiche, leiste: leisteAus(erlaubt), gruppen };
}

/** Je Gruppe ein Punkt an der Stelle ihrer ersten erlaubten Seite, mit dem ersten Zähler der Gruppe. */
function punkteAus(eintraege: readonly NavEintrag[]): NavPunkt[] {
  const punkte: NavPunkt[] = [];
  const gesehen = new Map<string, NavPunkt>();
  for (const e of eintraege) {
    if (!e.gruppe) {
      punkte.push({ ...e, pfade: [e.pfad] });
      continue;
    }
    const da = gesehen.get(e.gruppe);
    if (da) {
      da.pfade.push(e.pfad);
      da.zaehler ??= e.zaehler;
      continue;
    }
    const punkt: NavPunkt = { ...e, label: e.gruppe, pfade: [e.pfad] };
    gesehen.set(e.gruppe, punkt);
    punkte.push(punkt);
  }
  return punkte;
}

/** Untere Leiste am Handy: je Position die erlaubten Einträge nach Rang (Standard 50). Die Shell zeigt den ersten, dessen Bedingung
 *  gilt. Nach dem ersten ohne Bedingung kommt keiner mehr dran. „Mehr“ hängt die Shell an. */
function leisteAus(erlaubt: readonly NavEintrag[]): NavEintrag[][] {
  const jePosition = new Map<number, NavEintrag[]>();
  for (const e of erlaubt) {
    if (e.leiste === undefined) continue;
    jePosition.set(e.leiste, [...(jePosition.get(e.leiste) ?? []), e]);
  }
  return [...jePosition.entries()]
    .sort(([a], [b]) => a - b)
    .map(([, liste]) => {
      // sort ist stabil: Bei gleichem Rang bleibt die Reihenfolge der Module
      const nachRang = [...liste].sort((a, b) => (a.leisteRang ?? 50) - (b.leisteRang ?? 50));
      const ohneBedingung = nachRang.findIndex((e) => !(e.leisteBedingung ?? e.bedingung));
      return ohneBedingung === -1 ? nachRang : nachRang.slice(0, ohneBedingung + 1);
    });
}

/** Gemerkte Wahl zum Auf- und Zuklappen eines Bereichs, null ohne Wahl */
export type AufklappWahl = "offen" | "zu" | null;

/** Ist ein aufklappbarer Bereich offen? Vorgabe zu. Ohne eigene Wahl ist er offen, solange die aktuelle Seite darin liegt. Eine eigene
 *  Wahl gilt immer, auch auf seinen Seiten: „Zuklappen, neu laden: bleibt zu“ (BAUPLAN-SEITENLEISTE.md, Abschnitte 5 und 10). */
export function aufgeklappt(wahl: AufklappWahl, aktivDrin: boolean): boolean {
  return wahl === null ? aktivDrin : wahl === "offen";
}

/** Liegt der Pfad auf dieser Seite oder darunter (/protokolle/123 gehört zu /protokolle)? „/“ nur genau. */
export function pfadGehoertZu(jetzt: string, pfad: string): boolean {
  if (pfad === "/") return jetzt === "/";
  return jetzt === pfad || jetzt.startsWith(`${pfad}/`);
}

/** Darf die Person diese Route öffnen? */
export function routeErlaubt(recht: Recht | undefined, rechte: ReadonlySet<Recht>): boolean {
  return !recht || rechte.has(recht);
}
